"use client";

import { PanelRightClose, PanelRightOpen } from "lucide-react";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import styles from "./LessonBoardDock.module.css";
import { WhiteboardPhotoFrame } from "./WhiteboardPhotoFrame";

/** Desktop widths keep the conversation and the lesson board side by side. */
export const LESSON_DOCK_QUERY = "(min-width: 1024px)";

/**
 * Docks the board beside the conversation once the tutor first uses it in a
 * session, and keeps it docked for the rest of that session.
 */
export function useLessonBoardDock({
  eligible,
  boardActive,
}: {
  eligible: boolean;
  boardActive: boolean;
}) {
  const [wide, setWide] = useState(false);
  const [used, setUsed] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const query = window.matchMedia(LESSON_DOCK_QUERY);
    const update = () => setWide(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!eligible) setUsed(false);
    else if (boardActive) setUsed(true);
  }, [eligible, boardActive]);

  return eligible && wide && used;
}

export function LessonBoardDock({
  preparing,
  hasBoard,
  boardKey,
  children,
  footer,
}: {
  /** The tutor has started writing a board whose content has not arrived. */
  preparing: boolean;
  hasBoard: boolean;
  /** The current board's identity; a new board reopens a collapsed panel. */
  boardKey?: string;
  children?: ReactNode;
  /** Session-level material below the boards, such as the notebook. */
  footer?: ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const contentId = useId();
  const shownKey = useRef(boardKey);
  // Like a minimized board: the learner's choice holds through tutor updates,
  // but a new board opens the panel again.
  useEffect(() => {
    if (boardKey && boardKey !== shownKey.current) setCollapsed(false);
    shownKey.current = boardKey;
  }, [boardKey]);
  const presence = (
    <p className={styles.presence} role="status" aria-live="polite">
      <span className={styles.caret} aria-hidden="true" />
      {hasBoard ? "Chicky is writing a new board…" : "Chicky is writing on the board…"}
    </p>
  );
  return (
    <aside
      className={styles.dock}
      aria-label="Lesson board"
      data-lesson-dock
      data-collapsed={collapsed || undefined}
    >
      <div className={styles.dockHeader}>
        {collapsed ? null : <span className={styles.sectionTitle}>Lesson board</span>}
        <button
          type="button"
          className={styles.collapseToggle}
          aria-expanded={!collapsed}
          aria-controls={contentId}
          aria-label={
            collapsed
              ? preparing
                ? "Show lesson board, Chicky is writing"
                : "Show lesson board"
              : "Hide lesson board"
          }
          title={collapsed ? "Show lesson board" : "Hide lesson board"}
          onClick={() => setCollapsed((value) => !value)}
        >
          {collapsed ? (
            <PanelRightOpen size={18} aria-hidden="true" />
          ) : (
            <PanelRightClose size={18} aria-hidden="true" />
          )}
          {collapsed && preparing ? (
            <span className={styles.writingDot} aria-hidden="true" />
          ) : null}
        </button>
      </div>
      {/* Hidden, not unmounted: learner work and the board stream survive collapsing,
          and the board reports itself not visible to the tutor while hidden. */}
      <div id={contentId} className={styles.dockContent} hidden={collapsed}>
        {preparing && hasBoard ? presence : null}
        {preparing && !hasBoard ? (
          // The empty board frame appears first; its content fills in place.
          <WhiteboardPhotoFrame
            appearance="plain"
            className={`my-5 w-full max-w-3xl ${styles.placeholder}`}
            data-lesson-dock-placeholder
          >
            {presence}
          </WhiteboardPhotoFrame>
        ) : null}
        {children}
        {!hasBoard && !preparing ? (
          <p className={styles.empty}>Boards from this lesson stay here.</p>
        ) : null}
        {footer ? <div className={styles.notebookSlot}>{footer}</div> : null}
      </div>
    </aside>
  );
}
